<template>
  <v-container fluid class="pa-6 grey lighten-4 fill-height align-start">
    <v-row>
      <!-- LEFT PANEL: Invoice Header & Client Details -->
      <v-col cols="12" md="5" lg="4">
        <v-card outlined class="elevation-2 mb-4 pa-2">
          <v-toolbar flat color="primary" dark class="rounded-t">
            <v-toolbar-title class="headline font-weight-bold">
              Račun Fiskalizacija 1.0 - DEMO!!!
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pt-6">
            <!-- Inline Date and Time Fields -->
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="invoice.date"
                  label="Datum"
                  type="date"
                  outlined
                  class="accessible-field"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="invoice.time"
                  label="Vrijeme"
                  type="time"
                  outlined
                  class="accessible-field"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <!-- Toggle: Retail vs Company -->
            <v-btn-toggle
              v-model="customerType"
              mandatory
              color="primary"
              class="mb-6 d-flex border-all"
              height="54"
            >
              <v-btn value="private" class="flex-grow-1 text-none body-1 font-weight-medium">Privatni kupac</v-btn>
              <v-btn value="company" class="flex-grow-1 text-none body-1 font-weight-medium">Firma</v-btn>
            </v-btn-toggle>

            <!-- Adaptive Customer Inputs -->
            <v-form ref="customerForm">
              <v-slide-y-transition mode="out-in">
                <!-- Case A: Private Person -->
                <div v-if="customerType === 'private'" key="private">
                  <v-combobox
                    v-model="selectedPrivateClient"
                    :items="savedPrivateClients"
                    item-text="partnerName"
                    label="Traži/Dodaj privatnog kupca"
                    placeholder="Utipkajte ime..."
                    outlined
                    class="accessible-field"
                    clearable
                    @change="onPrivateClientSelect"
                  ></v-combobox>
                  
                  <v-text-field
                    v-if="showPrivateVat || invoice.vatId"
                    v-model="invoice.vatId"
                    label="Osobni OIB"
                    outlined
                    class="accessible-field"
                    append-icon="mdi-close"
                    @click:append="showPrivateVat = false; invoice.vatId = ''"
                  ></v-text-field>
                  <div v-else class="text-right mt-n4 mb-4">
                    <v-btn text color="primary" @click="showPrivateVat = true" class="body-1 font-weight-bold">
                      + Dodaj osobni OIB
                    </v-btn>
                  </div>
                </div>

                <!-- Case B: Company -->
                <div v-else key="company">
                  <v-combobox
                    v-model="selectedCompanyClient"
                    :items="savedCompanies"
                    item-text="Naziv firme"
                    label="Traži spremljenu Firmu"
                    outlined
                    class="accessible-field mb-4"
                    clearable
                    @change="onCompanySelect"
                  ></v-combobox>

                  <v-row class="align-center">
                    <v-col cols="8">
                      <v-text-field
                        v-model="invoice.vatId"
                        label="Firma OIB *"
                        outlined
                        class="accessible-field"
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-btn block color="secondary" height="56" outlined @click="verifyVatCompany" class="body-1">
                        PROVJERI
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-text-field
                    v-model="invoice.companyName"
                    label="Naziv firme *"
                    outlined
                    class="accessible-field mt-4"
                    hide-details
                  ></v-text-field>

                  <v-textarea
                    v-model="invoice.address"
                    label="Adresa"
                    outlined
                    rows="2"
                    class="accessible-field mt-4"
                    hide-details
                  ></v-textarea>
                </div>
              </v-slide-y-transition>

              <v-checkbox
                v-model="invoice.saveToDb"
                label="Spremi/Update u bazu"
                color="primary"
                class="mt-4 v-input--accessible-checkbox"
                hide-details
              ></v-checkbox>

              <v-divider class="my-4"></v-divider>

              <!-- Global Tax Flag -->
              <v-switch 
                v-model="invoice.applyVat" 
                color="success" 
                hide-details 
                label="Primjeni PDV"
                class="v-input--accessible-switch"
              ></v-switch>

              <v-divider class="my-4"></v-divider>

              <!-- Collapsible Comment Field -->
              <v-expansion-panels flat tile class="grey lighten-5 border-all">
                <v-expansion-panel class="transparent">
                  <v-expansion-panel-header class="px-4 py-3 body-1 font-weight-medium text--secondary">
                    <span><v-icon left>mdi-comment-text-outline</v-icon> Dodaj napomenu</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-textarea v-model="invoice.comment" outlined rows="2" class="accessible-field" hide-details></v-textarea>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT PANEL: Main Workspace (Search, Interactive Cart Matrix, Payments) -->
      <v-col cols="12" md="7" lg="8">
        <v-card outlined class="elevation-2 d-flex flex-column pa-2" style="min-height: 800px;">
          <v-toolbar flat color="grey lighten-3" class="rounded-t">
            <v-toolbar-title class="headline font-weight-bold grey--text text--darken-3">
              Stavke računa
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text class="flex-grow-1 pa-4 d-flex flex-column">
            <!-- Search & Add Items (DB Lookup or Custom string generation) -->
            <v-combobox
              v-model="itemSearchModel"
              :items="itemDatabase"
              item-text="itemName"
              label="Skeniraj kod ili upiši naziv ..."
              outlined
              prepend-inner-icon="mdi-barcode-scan"
              clearable
              return-object
              class="accessible-field mb-4"
              @change="handleItemSelection"
            >
              <template v-slot:no-data>
                <v-list-item @click="addCustomItemDirectly" class="py-2">
                  <v-list-item-content>
                    <v-list-item-title class="body-1">
                      Ne mogu pronaći. Kliknite  <strong class="primary--text">"{{ itemSearchModel }}"</strong> da spremite artikal/uslugu.
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>

            <!-- Interactive Spreadsheet Workspace Table (Enlarged Fonts and Paddings) -->
            <v-simple-table class="border-all flex-grow-1 accessible-table">
              <template v-slot:default>
                <thead>
                  <tr class="grey lighten-4">
                    <th class="text-left body-1 font-weight-bold" style="width: 30%;">Naziv</th>
                    <th class="text-center body-1 font-weight-bold" style="width: 10%;">Kol</th>
                    <th class="text-right body-1 font-weight-bold" style="width: 15%;">Neto cijena(€)</th>
                    <th class="text-center body-1 font-weight-bold" style="width: 15%;">PDV %</th>
                    <th class="text-right body-1 font-weight-bold" style="width: 12%;">Popust(€)</th>
                    <th class="text-right body-1 font-weight-bold" style="width: 15%;">Bruto (€)</th>
                    <th class="text-center" style="width: 3%;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cartItems" :key="index" class="table-row-large">
                    <!-- Item Name -->
                    <td>
                      <v-text-field v-model="item.name" hide-details class="pt-0 mt-0 plain-text-field body-1 font-weight-medium"></v-text-field>
                    </td>
                    <!-- Quantity -->
                    <td class="text-center">
                      <v-text-field v-model.number="item.qty" type="number" hide-details class="pt-0 mt-0 center-input body-1" style="width: 70px; margin: 0 auto;"></v-text-field>
                    </td>
                    <!-- Original Base Net Price -->
                    <td class="text-right">
                      <v-text-field v-model.number="item.basePrice" type="number" step="0.01" hide-details class="pt-0 mt-0 right-input body-1" style="width: 90px; margin-left: auto;" @input="recalculateFromBase(item)"></v-text-field>
                    </td>
                    <!-- Tax Rate Selection Tier Dropdown -->
                    <td class="text-center">
                      <v-select
                        v-model="item.taxRate"
                        :items="taxTiers"
                        item-text="label"
                        item-value="value"
                        hide-details
                        class="pt-0 mt-0 body-1 plain-text-field center-input font-weight-medium"
                        style="width: 110px; margin: 0 auto;"
                        @change="recalculateFromBase(item)"
                      ></v-select>
                    </td>
                    <!-- Flat Discount Input Field -->
                    <td class="text-right">
                      <v-text-field v-model.number="item.discount" type="number" step="0.01" hide-details class="pt-0 mt-0 right-input body-1 red--text font-weight-medium" style="width: 80px; margin-left: auto;" @input="recalculateFromDiscount(item)"></v-text-field>
                    </td>
                    <!-- Target Gross Input (Modifies values backwards to balance books natively) -->
                    <td class="text-right">
                      <v-text-field v-model.number="item.targetGross" type="number" step="0.01" hide-details class="pt-0 mt-0 right-input font-weight-bold generic-gross-field body-1" style="width: 100px; margin-left: auto;" @input="recalculateFromTargetGross(item)"></v-text-field>
                    </td>
                    <!-- Delete Item Row Button -->
                    <td class="text-center">
                      <v-btn icon color="error" @click="removeItem(index)"><v-icon>mdi-delete</v-icon></v-btn>
                    </td>
                  </tr>
                  <tr v-if="cartItems.length === 0">
                    <td colspan="7" class="text-center py-16 title grey--text text--darken-1">
                      Nema stavka na računu. Skenirajte kod ili upišite naziv...
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>

          <!-- BOTTOM SECTION: Multi-tax Itemized Recaps & Inline Card Payment Processor Matrix -->
          <v-card-text class="grey lighten-4 border-top pa-6">
            <v-row class="align-center">
              <!-- Tax Breakdown Block Engine summary profiles -->
              <v-col cols="12" md="5" class="d-flex flex-column justify-center py-0">
                <div class="d-flex justify-space-between mb-2 body-1 text--secondary">
                  <span>Neto ukupno:</span><span>{{ localMoney(totals.subtotal) }} €</span>
                </div>
                <!-- Mini Tax Ledger per tier layer -->
                <div v-for="(amount, rate) in totals.taxLedger" :key="rate" class="d-flex justify-space-between mb-2 body-1 grey--text text--darken-2">
                  <span>PDV stopa ({{ (rate * 100) }}%):</span><span>{{ localMoney(amount) }} €</span>
                </div>
                <v-divider class="my-3"></v-divider>
                <div class="d-flex justify-space-between align-center">
                  <span class="headline font-weight-bold">Ukupno:</span>
                  <span class="display-1 font-weight-black primary--text">{{ localMoney(totals.finalTotal) }} €</span>
                </div>
              </v-col>

              <!-- Payment Action Selection Grid Layout Matrix -->
              <v-col cols="12" md="7" class="border-left-desktop pl-md-6 py-0">
                <span class="title font-weight-bold text--secondary d-block mb-3">Vrsta plaćanja:</span>
                <v-item-group v-model="invoice.paymentMethod" mandatory>
                  <v-row dense>
                    <v-col v-for="method in paymentMethods" :key="method.value" cols="6" class="pa-2">
                      <v-item :value="method.value" v-slot:default>
                        <v-card
                          :color="invoice.paymentMethod === method.value ? 'primary' : 'white'"
                          :dark="invoice.paymentMethod === method.value"
                          outlined
                          class="text-center d-flex align-center justify-center cursor-pointer transition-swing elevation-1"
                          height="64"
                          @click="invoice.paymentMethod = method.value"
                        >
                          <span class="title font-weight-bold">
                            <v-icon left size="26" :dark="invoice.paymentMethod === method.value">{{ method.icon }}</v-icon>
                            {{ method.label }}
                          </span>
                        </v-card>
                      </v-item>
                    </v-col>
                  </v-row>
                </v-item-group>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Receipt Distribution Framework Toggle Layout -->
            <v-row class="align-center mb-4">
              <v-col cols="12" md="5">
                <v-btn-toggle v-model="deliveryMethod" mandatory color="info" class="d-flex border-all" height="54">
                  <v-btn value="print" class="flex-grow-1 text-none body-1 font-weight-medium"><v-icon left>mdi-printer</v-icon>Isprintaj</v-btn>
                  <v-btn value="email" class="flex-grow-1 text-none body-1 font-weight-medium"><v-icon left>mdi-email-outline</v-icon>Šalji na email</v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12" md="7">
                <v-slide-x-transition>
                  <v-text-field
                    v-if="deliveryMethod === 'email'"
                    v-model="invoice.email"
                    label="Email adresa kupca *"
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-at"
                    placeholder="kupac@primjer.com"
                    class="white accessible-field"
                  ></v-text-field>
                </v-slide-x-transition>
              </v-col>
            </v-row>

            <!-- Final Process Button execution hook -->
            <v-btn block color="success" height="70" class="headline font-weight-black mt-2 elevation-2" :disabled="cartItems.length === 0" @click="handleInvoiceSubmission">
              <v-icon left size="30">{{ deliveryMethod === 'email' ? 'mdi-email-send' : 'mdi-check-circle' }}</v-icon>
              {{ isCardPayment ? 'Šaljem na terminal' : (deliveryMethod === 'email' ? 'Završi i šalji email' : 'Završi i isprintaj') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ACCESSIBLE MODAL SYSTEM: Async Blocking Overlay Dialog with Large Readable Layouts -->
    <v-dialog v-model="isProcessingTerminal" persistent max-width="550">
      <v-card color="indigo darken-4" dark class="text-center pa-8 tile">
        <v-card-text class="pt-4 white--text">
          <div class="display-1 font-weight-bold mb-4">POS Terminal Aktivan</div>
          <v-progress-circular indeterminate color="white" size="80" width="8" class="mb-6"></v-progress-circular>
          <div class="headline mb-2 font-weight-medium">Čekam autorizaciju...</div>
          <div class="display-2 yellow--text font-weight-black tracking-wide my-6">{{ localMoney(totals.finalTotal) }} €</div>
          <p class="title text--lighten-2 font-weight-light line-height-large">Molim uputite kupca da prisloni karticu i upiše pin na POS terminalu.</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-2 mt-4">
          <v-btn color="error" x-large dark class="body-1 font-weight-bold px-6" outlined @click="abortTerminalPayment">Odustani & Vrati</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

import TeyaService from '@/services/TeyaService'
import { parseNum, round } from '@/snippets/allSnippets'
import moneyFormat from '@/snippets/moneyFormat'

export default {
  name: 'PosCompleteInvoiceEngine',
  data() {
    return {
      customerType: 'private',
      showPrivateVat: false,
      itemSearchModel: null,
      selectedPrivateClient: null,
      selectedCompanyClient: null,
      deliveryMethod: 'print',
      
      isProcessingTerminal: false,
      terminalTimeoutId: null,

      invoice: {
        date: new Date().toISOString().substr(0, 10),
        time: new Date().toTimeString().substr(0, 5),
        customerName: '',
        companyName: '',
        vatId: '',
        address: '',
        email: '',
        applyVat: true,
        comment: '',
        paymentMethod: 'cash',
        saveToDb: true
      },

      taxTiers: [
        { label: '25% VAT', value: 0.25 },
        { label: '13% VAT', value: 0.13 },
        { label: '5% VAT', value: 0.05 },
        { label: '0% Exempt', value: 0.00 }
      ],

      paymentMethods: [
        { label: 'Gotovina', value: 'cash', icon: 'mdi-cash' },
        { label: 'Visa', value: 'visa', icon: 'mdi-credit-card' },
        { label: 'Mastercard', value: 'master', icon: 'mdi-credit-card-outline' },
        { label: 'Žiro račun', value: 'bank', icon: 'mdi-bank' }
      ],

      itemDatabase: [
        // { name: 'Wireless Mouse M123', price: 35.00, taxRate: 0.25 },
        // { name: 'USB-C Hub (6-in-1)', price: 89.00, taxRate: 0.25 },
        // { name: 'HDMI Cable (3m)', price: 12.50, taxRate: 0.13 },
        // { name: 'Fresh Espresso Beans (1kg)', price: 24.00, taxRate: 0.05 },
        // { name: 'International Shipping Duty', price: 15.00, taxRate: 0.00 }
      ],

      savedPrivateClients: [
        // { name: 'John Doe', vatId: 'HR987654321', email: 'john.doe@gmail.com' },
        // { name: 'Anna Smith', vatId: '', email: 'anna.s@outlook.com' }
      ],
      savedCompanies: [
        // { companyName: 'TechCorp Solutions Inc.', vatId: 'HR12345678901', address: '123 Business Ave, Zagreb', email: 'billing@techcorp.hr' },
        // { companyName: 'Global Trade d.o.o.', vatId: 'HR55544433322', address: 'Slavonska Avenija 4, Split', email: 'accounting@globaltrade.hr' }
      ],

      cartItems: []
    };
  },
  mounted(){
    if (this.$store.state.partnerStore) {
      this.savedPrivateClients = this.$store.state.partnerStore.filter(par => par.privatePerson === true)
      this.savedCompanies = this.$store.state.partnerStore.filter(par => par.privatePerson === false)
    }
    this.itemDatabase = this.$store.state.itemStore ? this.$store.state.itemStore : []
    console.log('this.itemDatabase: ', this.itemDatabase);

  },
  computed: {
    isCardPayment() {
      return this.invoice.paymentMethod === 'visa' || this.invoice.paymentMethod === 'master';
    },

    totals() {
      let subtotal = 0;
      let taxLedger = { 0.25: 0, 0.13: 0, 0.05: 0, 0.00: 0 };

      this.cartItems.forEach(item => {
        const netItemUnitPrice = item.basePrice - item.discount;
        const netRowTotal = netItemUnitPrice * item.qty;
        
        subtotal += netRowTotal;

        if (this.invoice.applyVat && item.taxRate > 0) {
          const rowCalculatedTax = netRowTotal * item.taxRate;
          taxLedger[item.taxRate] += rowCalculatedTax;
        }
      });

      const totalTax = Object.values(taxLedger).reduce((acc, curr) => acc + curr, 0);
      const finalTotal = subtotal + totalTax;

      return { subtotal, taxLedger, totalTax, finalTotal };
    }
  },

  watch: {
    customerType() {
      this.selectedPrivateClient = null;
      this.selectedCompanyClient = null;
      this.invoice.vatId = '';
      this.invoice.companyName = '';
      this.invoice.customerName = '';
      this.invoice.address = '';
      this.invoice.email = '';
    }
  },

  methods: {
    recalculateFromBase(item) {
      console.log('item: ', item);
      
      const activeTaxRate = this.invoice.applyVat ? item.taxRate : 0;
      item.targetGross = Number(((item.basePrice - item.discount) * (1 + activeTaxRate)).toFixed(2));
    },
    localMoney(moneyToFormat) {
        return moneyFormat(moneyToFormat)
    },
    recalculateFromDiscount(item) {
      this.recalculateFromBase(item);
    },

    recalculateFromTargetGross(item) {
      if (!item.targetGross || item.targetGross <= 0) {
        item.discount = 0;
        return;
      }
      const activeTaxRate = this.invoice.applyVat ? item.taxRate : 0;
      const targetNetUnitPrice = item.targetGross / (1 + activeTaxRate);
      item.discount = Number((item.basePrice - targetNetUnitPrice).toFixed(4));
    },

    handleItemSelection(val) {
      if (!val) return;
      if (typeof val === 'object') {
        console.log('val objekt', val)
        this.addCartItem(val.itemName, val.itemPriceWithVAT, round((val.itemVAT1Percentage/100),2));
      }
      this.$nextTick(() => { this.itemSearchModel = null; });
    },
    addCustomItemDirectly() {
      if (!this.itemSearchModel) return;
      const cleanName = typeof this.itemSearchModel === 'string' ? this.itemSearchModel : this.itemSearchModel.name;
      this.addCartItem(cleanName, 0.00, 0.25);
      this.$nextTick(() => { this.itemSearchModel = null; });
    },
    addCartItem(name, basePrice, taxRate) {
      const existing = this.cartItems.find(i => i.name.toLowerCase() === name.toLowerCase() && i.taxRate === taxRate);
      if (existing) {
        existing.qty++;
        this.recalculateFromBase(existing);
      } else {
        let newItem = { name, qty: 1, basePrice, taxRate, discount: 0, targetGross: 0 };
        this.recalculateFromBase(newItem);
        this.cartItems.push(newItem);
      }
    },
    removeItem(idx) { 
      this.cartItems.splice(idx, 1); 
    },

    onPrivateClientSelect(val) {
      if (val && typeof val === 'object') {
        this.invoice.customerName = val.name;
        this.invoice.vatId = val.vatId;
        this.invoice.email = val.email || '';
      } else if (typeof val === 'string') {
        this.invoice.customerName = val;
      }
    },
    onCompanySelect(val) {
      if (val && typeof val === 'object') {
        this.invoice.companyName = val.partnerName;
        this.invoice.vatId = val.partnerVATID;
        this.invoice.address = val. partnerStreet + ' ' + val.partnerBuildingNameNumber + ', ' + val.partnerPostcode + ' ' + val.partnerCity;
        this.invoice.email = val.partneremail || '';
      }
    },
    getRndInteger(min, max) {
      return Math.floor(Math.random() * (max - min) ) + min;
    },

    verifyVatCompany() {
      alert(`Tražim partnera po OIBu: ${this.invoice.vatId}`);
    },

    handleInvoiceSubmission() {
      if (this.customerType === 'company' && (!this.invoice.companyName || !this.invoice.vatId)) {
        alert('Molim ispunite potrebna polja za firmu.');
        return;
      }
      if (this.deliveryMethod === 'email' && !this.invoice.email) {
        alert('Za slanje računa ne email, treba validna email adresa!');
        return;
      }

      if (this.isCardPayment) {
        this.executeTerminalHandshake();
      } else {
        this.commitInvoiceFinalData();
      }
    },

    async executeTerminalHandshake() {
      try {
        this.isProcessingTerminal = true;

        const sendQuery = {}
        sendQuery.CompanyId = this.$store.state.companyid ? this.$store.state.companyid : 1
        sendQuery.CompanyNameDatabase = this.$store.state.companyName ? this.$store.state.companyName : ''
        sendQuery.BusinessYear = this.$store.state.businessYear ? this.$store.state.businessYear : 2020
        sendQuery.documentName = 'POS'
        sendQuery.amount = 0.01
        sendQuery.invoiceId = this.getRndInteger(100000, 900000)
        sendQuery.posAppId = '1'
        sendQuery.posAppName = 'CEgrupa'
        sendQuery.DatumIzvrsenja = new Date()

        const res = TeyaService.initiate(sendQuery)
        console.log('TeyaService res: ', res);
        
        this.terminalTimeoutId = setTimeout(() => {
          this.isProcessingTerminal = false;
          alert('Terminal je verifcirao karticu. Plaćanje je potvrđeno. Možete zaključiti račun');
          this.commitInvoiceFinalData();
        }, 4500);
      } catch (err) {
        console.log('Teya initiat err: ', err);
        }
    },

    abortTerminalPayment() {
      clearTimeout(this.terminalTimeoutId);
      this.isProcessingTerminal = false;
      alert('Transakcija je prekinuta. Moguće je došlo do greške. Novac nije skinut!');
    },

    commitInvoiceFinalData() {
      const payload = {
        meta: { customerType: this.customerType, deliveryMethod: this.deliveryMethod, paymentType: this.invoice.paymentMethod },
        billing: { ...this.invoice },
        cart: this.cartItems,
        accounting: { ...this.totals }
      };
      
      console.log('Podaci u bazu uspješno upisani! ', payload);
      alert(this.deliveryMethod === 'email' ? 'email Račun je uspješno poslan!' : 'Račun je ispisan uspješno!');
      this.cartItems = [];
    }
  }
};
</script>

<style scoped>
.border-all { border: 2px solid rgba(0, 0, 0, 0.15) !important; }
.border-top { border-top: 2px solid rgba(0, 0, 0, 0.15) !important; }
.cursor-pointer { cursor: pointer; }
.line-height-large { line-height: 1.6; }

@media (min-width: 960px) {
  .border-left-desktop { border-left: 2px solid rgba(0, 0, 0, 0.15) !important; }
}

/* Accessible Forms: Increase font sizing for text fields, dropdown selections and input indicators */
.accessible-field >>> .v-label { font-size: 1.15rem !important; }
.accessible-field >>> input { font-size: 1.15rem !important; padding: 12px 0 !important; }
.accessible-field >>> .v-input__slot { min-height: 56px !important; }

/* Scale up custom checkboxes and switch text targets */
.v-input--accessible-checkbox >>> .v-label { font-size: 1.2rem !important; font-weight: 500; color: #424242 !important; }
.v-input--accessible-checkbox >>> .v-icon { font-size: 28px !important; }
.v-input--accessible-switch >>> .v-label { font-size: 1.2rem !important; font-weight: bold; color: #2e7d32 !important; }

/* Accessible Datatable Configurations */
.accessible-table th { height: 60px !important; font-size: 1.1rem !important; }
.table-row-large td { height: 70px !important; border-bottom: 1px solid rgba(0,0,0,0.12) !important; }

/* In-table raw spreadsheet configuration adjustments */
.plain-text-field >>> .v-input__slot:before, .plain-text-field >>> .v-input__slot:after { display:none !important; }
.plain-text-field >>> .v-input__slot { padding: 0 !important; }
.center-input >>> input { text-align: center; font-size: 1.15rem !important; }
.center-input >>> .v-select__selection { width: 100%; text-align: center; justify-content: center; font-size: 1.15rem !important; }
.right-input >>> input { text-align: right; font-size: 1.15rem !important; font-weight: 500; }
.generic-gross-field >>> input { color: #1565C0 !important; font-weight: bold; }
</style>